// This is a replacement for `src/rxdb/BorerOperatorStateFeed/queryBuilder.ts`
import { print } from 'graphql';
import { gql } from 'graphql-tag';
import { isString } from 'radash';
import type { MigrationStrategies, RxCollection, RxDocument, RxJsonSchema } from 'rxdb';

import { BaseEntity, isBaseEntity } from '../../../models/BaseEntity';
import { formatDateToUnix } from '../../../utilities/hooks/useDateFormatters';
import { generatePullQuery, PullQueryContext } from '../../queryBuilders/generatePullQuery';
import { RxdbCollectionNameV2 } from '../RxdbCollectionNameV2';

export interface BorerOperatorStateBase extends BaseEntity {
  readonly borerStateId: string;
  readonly borerStateTypeId: string;
  readonly comment: string | null;
  readonly startTime: string;
  readonly endTime: string | null;
  readonly borerOperatorStateRequestId: string | null;
  readonly borerShiftAdvanceId: string | null;
  readonly cuttingTypeId: string | null;
  readonly cuttingMethodId: string | null;
}

export function isBorerOperatorStateBase(obj: unknown): obj is BorerOperatorStateBase {
  if (!isBaseEntity(obj)) return false;
  const possible = obj as Partial<BorerOperatorStateBase>;
  if (!isString(possible.borerStateId)) return false;
  if (!isString(possible.borerStateTypeId)) return false;
  if (!(isString(possible.comment) || possible.comment === null)) return false;
  if (!isString(possible.startTime)) return false;
  if (!(isString(possible.endTime) || possible.endTime === null)) return false;
  if (
    !(
      isString(possible.borerOperatorStateRequestId) ||
      possible.borerOperatorStateRequestId === null
    )
  ) {
    return false;
  }
  if (!(isString(possible.borerShiftAdvanceId) || possible.borerShiftAdvanceId === null)) {
    return false;
  }
  if (!(isString(possible.cuttingTypeId) || possible.cuttingTypeId === null)) return false;
  if (!(isString(possible.cuttingMethodId) || possible.cuttingMethodId === null)) return false;
  return true;
}

export interface BorerOperatorState extends BorerOperatorStateBase {
  readonly startTimeUnix: number;
  readonly endTimeUnix?: number;
  readonly showInSchedulerView: boolean | null;
}
export type BorerOperatorStateCollection = RxCollection<BorerOperatorState> | null;
export type BorerOperatorStateDocument = RxDocument<BorerOperatorState>;

export const borerOperatorStateSchema: RxJsonSchema<BorerOperatorState> = {
  type: 'object',
  version: 3,
  description: 'describes a BorerOperatorState object',
  primaryKey: 'id',
  properties: {
    id: { type: 'string', maxLength: 36 },
    updatedAt: { type: 'number' },
    isDeleted: { type: 'boolean' },
    version: { type: 'number' },
    borerStateId: { type: 'string' },
    borerStateTypeId: {
      type: 'string',
      ref: RxdbCollectionNameV2.BORER_STATE_TYPE,
    },
    comment: { type: ['string', 'null'] },
    startTime: { type: 'string' },
    endTime: { type: ['string', 'null'] },
    borerOperatorStateRequestId: { type: ['string', 'null'] },
    startTimeUnix: {
      type: 'number',
      multipleOf: 1,
      minimum: 1,
      maximum: 9999999999999,
    },
    endTimeUnix: {
      type: ['number', 'null'],
      multipleOf: 1,
      minimum: 1,
      maximum: 9999999999999,
    },
    borerShiftAdvanceId: {
      type: ['string', 'null'],
      ref: RxdbCollectionNameV2.BORER_SHIFT_ADVANCE,
    },
    showInSchedulerView: { type: 'boolean' },
    cuttingTypeId: {
      type: ['string', 'null'],
      ref: RxdbCollectionNameV2.CUTTING_TYPE,
    },
    cuttingMethodId: {
      type: ['string', 'null'],
      ref: RxdbCollectionNameV2.CUTTING_METHOD,
    },
  },
  indexes: ['startTimeUnix'],
};

export const borerOperatorStateMigrationStrategy: MigrationStrategies = {
  1: (oldDoc: BorerOperatorStateDocument) => ({ ...oldDoc, borerShiftAdvanceId: null }),
  2: (oldDoc: BorerOperatorStateDocument) => ({ ...oldDoc, showInSchedulerView: true }),
  3: (oldDoc: BorerOperatorStateDocument) => ({
    ...oldDoc,
    cuttingTypeId: null,
    cuttingMethodId: null,
  }),
};

const borerOperatorStateFeedQuery = print(gql`
  query borerOperatorStateFeed($limit: Int!, $lastUpdateAt: Float!, $borerEquipmentId: ID!) {
    borerOperatorStateFeed(
      borerEquipmentId: $borerEquipmentId
      lastUpdateAt: $lastUpdateAt
      limit: $limit
    ) {
      borerStateId
      borerStateTypeId
      comment
      endTime
      id
      isDeleted
      startTime
      updatedAt
      version
      borerOperatorStateRequestId
      borerShiftAdvanceId
      cuttingTypeId
      cuttingMethodId
    }
  }
`);

export const borerOperatorStatePullQueryBuilder = generatePullQuery(
  borerOperatorStateFeedQuery,
  PullQueryContext.Borer,
);

export const borerOperatorStatePullModifier = (doc: BorerOperatorState) => {
  const result = {
    ...doc,
    startTimeUnix: formatDateToUnix(doc.startTime),
    endTimeUnix: doc.endTime ? formatDateToUnix(doc.endTime) : null,
    showInSchedulerView: true,
  };
  return result;
};
